// data/servicesData.js
import {
  FaCode,
  FaBrain,
  FaMobile,
  FaDesktop,
  FaPalette,
  FaChartLine,
  FaCheckCircle,
  FaUsers,
} from "react-icons/fa";

export const servicesData = {
  "software-development": {
    id: "software-development",
    icon: FaCode,
    title: "Software Development",
    subtitle: "Web, Mobile, Desktop",
    description:
      "Build powerful web applications, mobile apps, and desktop software tailored to your business needs. We leverage modern technologies to create scalable, performant solutions.",
    detailedDescription:
      "Our software development services cover the entire development lifecycle, from initial concept and design to deployment and maintenance. We specialize in creating custom solutions that address your unique business challenges and help you achieve your goals.",
    features: [
      "Custom Web Applications",
      "Mobile App Development (iOS & Android)",
      "Desktop Application Development",
      "Progressive Web Apps (PWA)",
      "API Development & Integration",
      "Cloud-Native Solutions",
      "Microservices Architecture",
      "Real-time Applications",
    ],
    benefits: [
      {
        title: "Scalability",
        description: "Built to grow with your business needs",
      },
      {
        title: "Performance",
        description: "Optimized for speed and efficiency",
      },
      {
        title: "Security",
        description: "Industry-standard security practices",
      },
      {
        title: "Maintenance",
        description: "Ongoing support and updates",
      },
    ],
    technologies: [
      "React.js",
      "Node.js",
      "Python",
      "Java",
      "C#",
      ".NET",
      "MongoDB",
      "PostgreSQL",
      "AWS",
      "Azure",
      "Docker",
      "Kubernetes",
    ],
    process: [
      {
        title: "Discovery",
        description: "Understanding your requirements and goals",
      },
      {
        title: "Design",
        description: "Creating wireframes and architecture",
      },
      {
        title: "Development",
        description: "Building your solution with best practices",
      },
      {
        title: "Deployment",
        description: "Launch and ongoing support",
      },
    ],
    gradient: "from-blue-500 to-cyan-500",
    heroImage:
      "https://images.unsplash.com/photo-1555066931-4365d14bab8c?auto=format&fit=crop&q=80",
  },
  "ai-ml": {
    id: "ai-ml",
    icon: FaBrain,
    title: "AI & Machine Learning",
    subtitle: "Automation, Insights",
    description:
      "Harness the power of artificial intelligence and machine learning to automate processes, gain insights, and make data-driven decisions.",
    detailedDescription:
      "Transform your business with cutting-edge AI and machine learning solutions. We help you leverage data to create intelligent systems that learn, adapt, and improve over time.",
    features: [
      "Machine Learning Models",
      "Natural Language Processing",
      "Computer Vision Solutions",
      "Predictive Analytics",
      "Intelligent Automation",
      "AI-Powered Chatbots",
      "Deep Learning",
      "Data Mining",
    ],
    benefits: [
      {
        title: "Automation",
        description: "Reduce manual work and increase efficiency",
      },
      {
        title: "Insights",
        description: "Data-driven decision making",
      },
      {
        title: "Prediction",
        description: "Forecast trends and outcomes",
      },
      {
        title: "Personalization",
        description: "Tailored user experiences",
      },
    ],
    technologies: [
      "TensorFlow",
      "PyTorch",
      "Scikit-learn",
      "OpenAI",
      "Hugging Face",
      "Pandas",
      "NumPy",
      "Keras",
      "NLTK",
      "OpenCV",
      "MLflow",
      "Apache Spark",
    ],
    process: [
      {
        title: "Data Collection",
        description: "Gathering and preparing datasets",
      },
      {
        title: "Model Training",
        description: "Building and training AI models",
      },
      {
        title: "Testing",
        description: "Validating model accuracy",
      },
      {
        title: "Integration",
        description: "Deploying into production",
      },
    ],
    gradient: "from-purple-500 to-pink-500",
    heroImage:
      "https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&q=80",
  },
  "ui-ux-design": {
    id: "ui-ux-design",
    icon: FaPalette,
    title: "UI/UX Design",
    subtitle: "User Interface, Brand Identity",
    description:
      "Create stunning, user-friendly interfaces that delight your customers. Our design team crafts experiences that drive engagement and conversion.",
    detailedDescription:
      "We create beautiful, intuitive designs that put your users first. Our design process combines creativity with user research to deliver experiences that are both aesthetically pleasing and highly functional.",
    features: [
      "User Interface Design",
      "User Experience Research",
      "Wireframing & Prototyping",
      "Brand Identity Design",
      "Responsive Design",
      "Design Systems",
      "Usability Testing",
      "Interaction Design",
    ],
    benefits: [
      {
        title: "User Satisfaction",
        description: "Intuitive interfaces users love",
      },
      {
        title: "Brand Consistency",
        description: "Cohesive design across platforms",
      },
      {
        title: "Conversion",
        description: "Optimized for business goals",
      },
      {
        title: "Accessibility",
        description: "Inclusive design for all users",
      },
    ],
    technologies: [
      "Figma",
      "Adobe XD",
      "Sketch",
      "Adobe Illustrator",
      "After Effects",
      "InVision",
      "Framer",
      "Principle",
      "Zeplin",
      "Miro",
      "Photoshop",
      "Webflow",
    ],
    process: [
      {
        title: "Research",
        description: "Understanding user needs",
      },
      {
        title: "Wireframing",
        description: "Creating low-fidelity prototypes",
      },
      {
        title: "Design",
        description: "High-fidelity mockups",
      },
      {
        title: "Testing",
        description: "User feedback and iteration",
      },
    ],
    gradient: "from-pink-500 to-rose-500",
    heroImage:
      "https://images.unsplash.com/photo-1561070791-2526d30994b5?auto=format&fit=crop&q=80",
  },
  "mobile-development": {
    id: "mobile-development",
    icon: FaMobile,
    title: "Mobile Development",
    subtitle: "iOS, Android, Cross-platform",
    description:
      "Native and cross-platform mobile applications that deliver exceptional performance and user experience across all devices.",
    detailedDescription:
      "Build powerful mobile applications that reach users wherever they are. We develop native iOS and Android apps, as well as cross-platform solutions that work seamlessly across devices.",
    features: [
      "Native iOS Development",
      "Native Android Development",
      "React Native Apps",
      "Flutter Development",
      "Mobile App UI/UX",
      "App Store Optimization",
      "Push Notifications",
      "Offline Functionality",
    ],
    benefits: [
      {
        title: "Native Performance",
        description: "Fast, smooth user experience",
      },
      {
        title: "Cross-Platform",
        description: "Single codebase, multiple platforms",
      },
      {
        title: "App Store Ready",
        description: "Optimized for submission",
      },
      {
        title: "Device Integration",
        description: "Camera, GPS, sensors, and more",
      },
    ],
    technologies: [
      "Swift",
      "Kotlin",
      "React Native",
      "Flutter",
      "Dart",
      "Xcode",
      "Android Studio",
      "Firebase",
      "SQLite",
      "Realm",
      "GraphQL",
      "Redux",
    ],
    process: [
      {
        title: "Planning",
        description: "Define features and platform",
      },
      {
        title: "Development",
        description: "Build native or cross-platform",
      },
      {
        title: "Testing",
        description: "QA across devices",
      },
      {
        title: "Launch",
        description: "App store submission",
      },
    ],
    gradient: "from-cyan-500 to-teal-500",
    heroImage:
      "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&q=80",
  },
  erp: {
    id: "erp",
    icon: FaDesktop,
    title: "Enterprise ERP",
    subtitle: "Enterprise, Cloud",
    description:
      "Streamline your business operations with comprehensive ERP solutions. Integrate all aspects of your business into one unified system.",
    detailedDescription:
      "Implement enterprise resource planning systems that unify your business processes. Our ERP solutions help you manage operations, finance, supply chain, and more from a single platform.",
    features: [
      "Custom ERP Development",
      "Cloud-Based ERP",
      "ERP Integration",
      "Business Process Automation",
      "Real-time Analytics",
      "Scalable Architecture",
      "Multi-module Systems",
      "Data Migration",
    ],
    benefits: [
      {
        title: "Integration",
        description: "Unified business processes",
      },
      {
        title: "Efficiency",
        description: "Automated workflows",
      },
      {
        title: "Visibility",
        description: "Real-time business insights",
      },
      {
        title: "Compliance",
        description: "Regulatory adherence",
      },
    ],
    technologies: [
      "SAP",
      "Oracle",
      "Microsoft Dynamics",
      "Odoo",
      "NetSuite",
      "Salesforce",
      "ERPNext",
      "PostgreSQL",
      "Redis",
      "Kafka",
      "RabbitMQ",
      "Elasticsearch",
    ],
    process: [
      {
        title: "Analysis",
        description: "Business requirements gathering",
      },
      {
        title: "Customization",
        description: "Tailoring to your needs",
      },
      {
        title: "Implementation",
        description: "System deployment",
      },
      {
        title: "Training",
        description: "User onboarding and support",
      },
    ],
    gradient: "from-indigo-500 to-purple-500",
    heroImage:
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80",
  },
  "digital-marketing": {
    id: "digital-marketing",
    icon: FaChartLine,
    title: "Digital Marketing",
    subtitle: "SEO, Growth",
    description:
      "Grow your online presence with data-driven digital marketing strategies. From SEO to social media, we help you reach your target audience.",
    detailedDescription:
      "Boost your brand visibility and drive meaningful engagement through strategic digital marketing. We create data-driven campaigns that deliver measurable results.",
    features: [
      "Search Engine Optimization",
      "Content Marketing",
      "Social Media Marketing",
      "Pay-Per-Click Advertising",
      "Email Marketing Campaigns",
      "Analytics & Reporting",
      "Conversion Optimization",
      "Influencer Marketing",
    ],
    benefits: [
      {
        title: "Visibility",
        description: "Increased online presence",
      },
      {
        title: "Traffic",
        description: "More qualified visitors",
      },
      {
        title: "Leads",
        description: "Higher conversion rates",
      },
      {
        title: "ROI",
        description: "Measurable business growth",
      },
    ],
    technologies: [
      "Google Analytics",
      "Google Ads",
      "Facebook Ads",
      "SEMrush",
      "Ahrefs",
      "Mailchimp",
      "HubSpot",
      "Hootsuite",
      "Buffer",
      "Moz",
      "Hotjar",
      "Tableau",
    ],
    process: [
      {
        title: "Strategy",
        description: "Define goals and audience",
      },
      {
        title: "Campaign",
        description: "Create and launch campaigns",
      },
      {
        title: "Monitor",
        description: "Track performance metrics",
      },
      {
        title: "Optimize",
        description: "Continuous improvement",
      },
    ],
    gradient: "from-orange-500 to-red-500",
    heroImage:
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80",
  },
  testing: {
    id: "testing",
    icon: FaCheckCircle,
    title: "End to End Testing",
    subtitle: "QA, Security",
    description:
      "Ensure quality and security with comprehensive testing services. We identify and fix issues before they reach your users.",
    detailedDescription:
      "Deliver flawless software with our comprehensive testing services. We ensure your applications are bug-free, secure, and perform optimally under all conditions.",
    features: [
      "Automated Testing",
      "Manual Testing",
      "Performance Testing",
      "Security Testing",
      "User Acceptance Testing",
      "Continuous Integration Testing",
      "Load Testing",
      "Regression Testing",
    ],
    benefits: [
      {
        title: "Quality",
        description: "Bug-free software delivery",
      },
      {
        title: "Security",
        description: "Vulnerability detection",
      },
      {
        title: "Performance",
        description: "Optimized under load",
      },
      {
        title: "Confidence",
        description: "Peace of mind for deployment",
      },
    ],
    technologies: [
      "Selenium",
      "Jest",
      "Cypress",
      "JUnit",
      "Mocha",
      "Postman",
      "JMeter",
      "Burp Suite",
      "TestRail",
      "Appium",
      "SonarQube",
      "OWASP ZAP",
    ],
    process: [
      {
        title: "Planning",
        description: "Define test strategy",
      },
      {
        title: "Execution",
        description: "Run test cases",
      },
      {
        title: "Reporting",
        description: "Document findings",
      },
      {
        title: "Verification",
        description: "Confirm fixes",
      },
    ],
    gradient: "from-green-500 to-emerald-500",
    heroImage:
      "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&q=80",
  },
  staffing: {
    id: "staffing",
    icon: FaUsers,
    title: "Contract Staffing",
    subtitle: "Expert Resources",
    description:
      "Access skilled professionals on-demand. We provide talented developers, designers, and specialists to augment your team.",
    detailedDescription:
      "Scale your team quickly with top-tier tech talent. Our contract staffing services provide you with skilled professionals who integrate seamlessly into your existing workflows.",
    features: [
      "Full-Stack Developers",
      "Frontend Specialists",
      "Backend Engineers",
      "DevOps Engineers",
      "UI/UX Designers",
      "Project Managers",
      "QA Engineers",
      "Data Scientists",
    ],
    benefits: [
      {
        title: "Flexibility",
        description: "Scale team as needed",
      },
      {
        title: "Expertise",
        description: "Skilled professionals",
      },
      {
        title: "Cost-Effective",
        description: "No long-term commitments",
      },
      {
        title: "Quick Onboarding",
        description: "Start within days",
      },
    ],
    technologies: [
      "React",
      "Angular",
      "Vue.js",
      "Node.js",
      "Python",
      "Java",
      "AWS",
      "Azure",
      "GCP",
      "Docker",
      "Kubernetes",
      "CI/CD",
    ],
    process: [
      {
        title: "Requirements",
        description: "Define your needs",
      },
      {
        title: "Matching",
        description: "Find the right talent",
      },
      {
        title: "Onboarding",
        description: "Quick integration",
      },
      {
        title: "Management",
        description: "Ongoing support",
      },
    ],
    gradient: "from-yellow-500 to-orange-500",
    heroImage:
      "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&q=80",
  },
};
